/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import * as components from "../components/index.js";
import { TeralError } from "./teralerror.js";

/**
 * Error response when one or more conditions specified in the request headers are not met. This typically occurs when:
 *
 * @remarks
 * - Using conditional requests with If-Match or If-None-Match headers
 * - The resource version doesn't match the expected value
 * - Optimistic concurrency control detects a conflict
 *
 * To resolve this error, fetch the latest version of the resource and retry with updated conditions.
 */
export type PreconditionFailedErrorResponseData = {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;
};

/**
 * Error response when one or more conditions specified in the request headers are not met. This typically occurs when:
 *
 * @remarks
 * - Using conditional requests with If-Match or If-None-Match headers
 * - The resource version doesn't match the expected value
 * - Optimistic concurrency control detects a conflict
 *
 * To resolve this error, fetch the latest version of the resource and retry with updated conditions.
 */
export class PreconditionFailedErrorResponse extends TeralError {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;

  /** The original data that was passed to this error instance. */
  data$: PreconditionFailedErrorResponseData;

  constructor(
    err: PreconditionFailedErrorResponseData,
    httpMeta: { response: Response; request: Request; body: string },
  ) {
    const message = "message" in err && typeof err.message === "string"
      ? err.message
      : `API error occurred: ${JSON.stringify(err)}`;
    super(message, httpMeta);
    this.data$ = err;
    this.meta = err.meta;
    this.error = err.error;

    this.name = "PreconditionFailedErrorResponse";
  }
}

/** @internal */
export const PreconditionFailedErrorResponse$inboundSchema: z.ZodType<
  PreconditionFailedErrorResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  meta: components.Meta$inboundSchema,
  error: components.BaseError$inboundSchema,
  request$: z.instanceof(Request),
  response$: z.instanceof(Response),
  body$: z.string(),
})
  .transform((v) => {
    return new PreconditionFailedErrorResponse(v, {
      request: v.request$,
      response: v.response$,
      body: v.body$,
    });
  });
